// src/components/books/modals/IntegrationModal.jsx
import React, { useState } from 'react';
import { X, HelpCircle, Database, Brain, Mail, Globe } from 'lucide-react';

export const IntegrationModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  currentValue = '', 
  integrationType = '' 
}) => {
  const [config, setConfig] = useState(() => {
    try {
      return currentValue ? JSON.parse(currentValue) : {};
    } catch {
      return { raw: currentValue };
    }
  });
  const [showHelper, setShowHelper] = useState(false);

  const integrationTypes = {
    'AI': {
      icon: Brain,
      title: 'AI Integration',
      description: 'Connect to AI services for dynamic responses',
      color: 'purple',
      fields: [
        { key: 'provider', label: 'AI Provider', type: 'select', options: ['OpenAI', 'Claude', 'Gemini'], required: true },
        { key: 'model', label: 'Model', type: 'text', placeholder: 'gpt-4, claude-3-opus, etc.' },
        { key: 'prompt', label: 'Prompt Template', type: 'textarea', placeholder: 'You are a helpful assistant. User said: {userInput}' },
        { key: 'temperature', label: 'Temperature', type: 'number', min: 0, max: 2, step: 0.1, default: 0.7 },
        { key: 'maxTokens', label: 'Max Tokens', type: 'number', default: 150 },
        { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Your API key' }
      ]
    },
    'Google Sheets': {
      icon: Database,
      title: 'Google Sheets Integration',
      description: 'Read from and write to Google Sheets',
      color: 'green',
      fields: [
        { key: 'action', label: 'Action', type: 'select', options: ['read', 'write', 'append'], required: true },
        { key: 'spreadsheetId', label: 'Spreadsheet ID', type: 'text', placeholder: 'Found in the Google Sheets URL', required: true },
        { key: 'range', label: 'Range', type: 'text', placeholder: 'Sheet1!A1:C10', required: true },
        { key: 'headers', label: 'Use Headers', type: 'checkbox', default: true },
        { key: 'mapping', label: 'Field Mapping', type: 'textarea', placeholder: 'name: {userName}\nemail: {userEmail}' }
      ]
    },
    'Webhook': {
      icon: Globe,
      title: 'Webhook Integration',
      description: 'Send data to external services via HTTP',
      color: 'blue',
      fields: [
        { key: 'url', label: 'Webhook URL', type: 'text', placeholder: 'https://your-service.com/webhook', required: true },
        { key: 'method', label: 'HTTP Method', type: 'select', options: ['POST', 'PUT', 'PATCH'], default: 'POST' },
        { key: 'headers', label: 'Custom Headers', type: 'textarea', placeholder: 'Authorization: Bearer token\nContent-Type: application/json' },
        { key: 'payload', label: 'Payload Template', type: 'textarea', placeholder: '{\n  "name": "{userName}",\n  "email": "{userEmail}"\n}' },
        { key: 'onSuccess', label: 'On Success Action', type: 'text', placeholder: 'jump:next_message' },
        { key: 'onError', label: 'On Error Action', type: 'text', placeholder: 'jump:error_message' }
      ]
    },
    'Email': {
      icon: Mail,
      title: 'Email Integration',
      description: 'Send automated emails',
      color: 'red',
      fields: [
        { key: 'provider', label: 'Email Provider', type: 'select', options: ['SendGrid', 'Mailgun', 'SMTP'], required: true },
        { key: 'to', label: 'Recipient', type: 'text', placeholder: '{userEmail} or specific@email.com', required: true },
        { key: 'from', label: 'From Email', type: 'text', placeholder: 'noreply@yoursite.com', required: true },
        { key: 'subject', label: 'Subject', type: 'text', placeholder: 'Thank you {userName}!', required: true },
        { key: 'template', label: 'Email Template', type: 'select', options: ['welcome', 'confirmation', 'reminder', 'custom'] },
        { key: 'body', label: 'Email Body', type: 'textarea', placeholder: 'HTML or plain text email content' },
        { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Your email service API key' }
      ]
    }
  };

  const currentIntegration = integrationTypes[integrationType];

  const updateConfig = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    const configString = JSON.stringify(config, null, 2);
    onSave(configString);
  };

  const renderField = (field) => {
    const value = config[field.key] || field.default || '';

    switch (field.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => updateConfig(field.key, e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2"
          >
            <option value="">Select {field.label}...</option>
            {field.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => updateConfig(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="w-full border border-gray-300 rounded px-3 py-2"
            rows="4"
          />
        );

      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => updateConfig(field.key, e.target.checked)}
            className="rounded border-gray-300"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => updateConfig(field.key, e.target.value)}
            placeholder={field.placeholder}
            min={field.min}
            max={field.max}
            step={field.step}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
        );

      case 'password':
        return (
          <input
            type="password"
            value={value}
            onChange={(e) => updateConfig(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateConfig(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-90vh overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            {currentIntegration?.icon && (
              <currentIntegration.icon className={`h-6 w-6 text-${currentIntegration.color}-600`} />
            )}
            <div>
              <h2 className="text-lg font-semibold">
                {currentIntegration?.title || 'Integration Configuration'}
              </h2>
              <p className="text-sm text-gray-600">
                {currentIntegration?.description || 'Configure integration parameters'}
              </p>
            </div>
            <button
              onClick={() => setShowHelper(!showHelper)}
              className="p-1 text-gray-400 hover:text-blue-600"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Helper Guide */}
          {showHelper && (
            <div className={`bg-${currentIntegration?.color || 'blue'}-50 rounded-lg p-4`}>
              <h3 className={`font-medium text-${currentIntegration?.color || 'blue'}-900 mb-2`}>
                Integration Guide
              </h3>
              <ul className={`text-sm text-${currentIntegration?.color || 'blue'}-800 space-y-1`}>
                <li>• Configure the integration to connect with external services</li>
                <li>• Use variables from user responses in your configuration</li>
                <li>• Test your integration before going live</li>
                <li>• Check service documentation for specific parameter requirements</li>
              </ul>
            </div>
          )}

          {/* Configuration Form */}
          {currentIntegration ? (
            <div className="space-y-4">
              {currentIntegration.fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                  {field.placeholder && field.type !== 'select' && (
                    <p className="text-xs text-gray-500 mt-1">{field.placeholder}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Please select an integration type first</p>
            </div>
          )}

          {/* Raw JSON Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Raw Configuration (JSON)
            </label>
            <textarea
              value={JSON.stringify(config, null, 2)}
              onChange={(e) => {
                try {
                  setConfig(JSON.parse(e.target.value));
                } catch {
                  // Invalid JSON, don't update
                }
              }}
              className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm"
              rows="8"
            />
          </div>

          {/* Integration Examples */}
          {currentIntegration && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium mb-2">Example Configuration</h3>
              <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
{integrationType === 'AI' && `{
  "provider": "OpenAI",
  "model": "gpt-4",
  "prompt": "Answer this question: {userQuestion}",
  "temperature": 0.7,
  "maxTokens": 150
}`}
{integrationType === 'Google Sheets' && `{
  "action": "append",
  "spreadsheetId": "1ABC123...",
  "range": "Sheet1!A:C",
  "mapping": "name: {userName}\\nemail: {userEmail}"
}`}
{integrationType === 'Webhook' && `{
  "url": "https://api.example.com/webhook",
  "method": "POST",
  "payload": "{\\n  \\"name\\": \\"{userName}\\",\\n  \\"email\\": \\"{userEmail}\\"\\n}"
}`}
{integrationType === 'Email' && `{
  "provider": "SendGrid",
  "to": "{userEmail}",
  "from": "noreply@yoursite.com",
  "subject": "Welcome {userName}!",
  "template": "welcome"
}`}
              </pre>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};